import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/shared/utils/cn'
import { Icon } from '@/shared/ui/Icon'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { useAppState } from '@/app/providers/AppStateProvider'
import {
  DRAFT_CONVERSATION_ID,
  useConversations,
} from '../ConversationsProvider'
import type { ConversationDTO } from '@/shared/api'

interface ConversationGroup {
  key: 'pinned' | 'history'
  label: string
  iconName: 'pin' | 'clock'
  conversations: ConversationDTO[]
}

function groupConversations(items: ConversationDTO[]): ConversationGroup[] {
  const pinned = items.filter((c) => c.pinned_at)
  const history = items.filter((c) => !c.pinned_at)
  return [
    { key: 'pinned', label: 'Fixadas', iconName: 'pin', conversations: pinned },
    { key: 'history', label: 'Histórico', iconName: 'clock', conversations: history },
  ]
}

/**
 * 7.8I — sidebar conversation list.
 *
 * Adds inline rename (pencil row-action) and a delete confirmation dialog.
 * Both are mounted per-list rather than per-row so we don't carry the
 * dialog overhead on every <li>.
 */
export function ConversationList() {
  const {
    list,
    listStatus,
    listError,
    activeConversationId,
    selectConversation,
    refreshList,
    deleteConversation,
    renameConversation,
  } = useConversations()
  const { setWorkspace } = useAppState()

  const handleSelectConversation = useCallback(
    (id: string) => {
      setWorkspace('chat')
      selectConversation(id)
    },
    [selectConversation, setWorkspace],
  )

  const groups = useMemo(() => groupConversations(list), [list])
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [pendingDelete, setPendingDelete] = useState<ConversationDTO | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (renamingId) inputRef.current?.select()
  }, [renamingId])

  const startRename = useCallback((c: ConversationDTO) => {
    setRenamingId(c.id)
    setDraftTitle(c.title)
  }, [])

  const commitRename = useCallback(
    (c: ConversationDTO) => {
      const next = draftTitle.trim()
      if (next && next !== c.title) {
        void renameConversation(c.id, next)
      }
      setRenamingId(null)
    },
    [draftTitle, renameConversation],
  )

  const cancelRename = useCallback(() => {
    setRenamingId(null)
  }, [])

  if (listStatus === 'loading' && list.length === 0) {
    return (
      <nav className="nav" aria-label="Conversas">
        <div className="px-4 py-6 text-center text-[12px] text-text-mute">
          Carregando conversas…
        </div>
      </nav>
    )
  }

  if (listStatus === 'error') {
    return (
      <nav className="nav" aria-label="Conversas">
        <div className="mx-3 my-3 rounded-lg border border-red-400/20 bg-red-400/5 p-3 text-[12px] text-red-200">
          <p className="m-0">{listError ?? 'Erro ao carregar conversas.'}</p>
          <button
            type="button"
            className="mt-2 text-[11.5px] font-medium text-violet underline"
            onClick={() => void refreshList()}
          >
            Tentar novamente
          </button>
        </div>
      </nav>
    )
  }

  if (list.length === 0) {
    return (
      <nav className="nav" aria-label="Conversas">
        <div className="px-4 py-6 text-center text-[12px] leading-relaxed text-text-mute">
          Nenhuma conversa ainda. Comece uma nova consulta para popular este
          histórico.
        </div>
      </nav>
    )
  }

  return (
    <>
      <nav className="nav" aria-label="Conversas">
        {groups.map((group) =>
          group.conversations.length === 0 ? null : (
            <section key={group.key} className="nav-section">
              <h3>
                <Icon name={group.iconName} />
                {group.label}
              </h3>
              <ul>
                {group.conversations.map((c) => {
                  const active =
                    c.id === activeConversationId &&
                    activeConversationId !== DRAFT_CONVERSATION_ID
                  const isRenaming = renamingId === c.id
                  return (
                    <li key={c.id}>
                      <div
                        className={cn('nav-item', { active })}
                        title={c.title}
                      >
                        <span className="dot" />
                        {isRenaming ? (
                          <input
                            ref={inputRef}
                            className="rename-input"
                            value={draftTitle}
                            maxLength={120}
                            onChange={(e) => setDraftTitle(e.target.value)}
                            onBlur={() => commitRename(c)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                commitRename(c)
                              } else if (e.key === 'Escape') {
                                e.preventDefault()
                                cancelRename()
                              }
                            }}
                            aria-label="Renomear conversa"
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <button
                            type="button"
                            className="label"
                            onClick={() => handleSelectConversation(c.id)}
                            style={{
                              all: 'unset',
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'pointer',
                            }}
                          >
                            {c.title}
                          </button>
                        )}
                        {!isRenaming && (
                          <span className="row-actions">
                            <button
                              type="button"
                              className="row-action"
                              title="Renomear"
                              aria-label="Renomear conversa"
                              onClick={(e) => {
                                e.stopPropagation()
                                startRename(c)
                              }}
                            >
                              <Icon name="edit" />
                            </button>
                            <button
                              type="button"
                              className="row-action"
                              title="Apagar"
                              aria-label="Apagar conversa"
                              onClick={(e) => {
                                e.stopPropagation()
                                setPendingDelete(c)
                              }}
                            >
                              <Icon name="trash" />
                            </button>
                          </span>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>
          ),
        )}
      </nav>

      <ConfirmDialog
        open={pendingDelete !== null}
        destructive
        title="Apagar conversa?"
        message={
          pendingDelete
            ? `Esta ação remove "${pendingDelete.title}" permanentemente do histórico desta organização.`
            : ''
        }
        confirmLabel="Apagar"
        cancelLabel="Cancelar"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) void deleteConversation(pendingDelete.id)
          setPendingDelete(null)
        }}
      />
    </>
  )
}
